import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';

import CommonModal from '../../../../components/common-modal/CommonModal';
import validate from '../../../../utils/validate';
import { addDatasetValidation } from './validation';
import SonarDatasetModalForm from './SonarDatasetModalForm';
import { getDatasetItem } from '../../../../redux/sonar-management/sonarManagementActions';
import arrayToDropdownValues from '../../../../utils/arrayToDropdownValues';
import { resetEditableDataset } from '../../../../redux/sonar-management/sonarManagementReducer';

import scss from './SonarDatasetModal.module.scss';

const SonarDatasetModal = ({ isOpen, onClose, onAdd, onEdit, datasetId }) => {
  const dispatch = useDispatch();
  const isEdit = useMemo(() => Boolean(datasetId), [datasetId]);

  const editableDataset = useSelector((state) => state.sonarManagement.editableDataset);

  const getSocialsString = useCallback(
    (fieldKey) => {
      if (!editableDataset?.socials?.[fieldKey]) {
        return '';
      }

      return editableDataset.socials[fieldKey].map(({ url }) => url).join(', ');
    },
    [editableDataset],
  );

  const addDatasetInitialValues = useMemo(
    () => ({
      country: editableDataset.country ?? 'Denmark',
      brandCompany: editableDataset.brandCompany ?? '',
      domain: editableDataset.domain ?? '',
      competitorType: editableDataset.competitorType ?? 'All',
      inEcosystem: editableDataset.inEcosystem ?? false,
      inFocus: editableDataset.inFocus ?? false,
      myCompany: editableDataset.myCompany ?? false,

      categories: arrayToDropdownValues(editableDataset.categories) ?? [],
      products: arrayToDropdownValues(editableDataset.products) ?? [],
      services: arrayToDropdownValues(editableDataset.services) ?? [],
      groups: arrayToDropdownValues(editableDataset.groups) ?? [],

      FACEBOOK: getSocialsString('FACEBOOK'),
      INSTAGRAM: getSocialsString('INSTAGRAM'),
      YOUTUBE: getSocialsString('YOUTUBE'),
      TWITTER: getSocialsString('TWITTER'),
      TIKTOK: getSocialsString('TIKTOK'),
      LINKEDIN: getSocialsString('LINKEDIN'),
    }),
    [editableDataset, getSocialsString],
  );

  const handleAddDataset = async (values) => {
    if (onAdd) {
      onAdd(values);
    }
  };

  const handleEditDataset = async (values) => {
    if (onEdit) {
      onEdit(values);
    }
  };

  useEffect(() => {
    if (datasetId) {
      dispatch(getDatasetItem(datasetId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasetId]);

  useEffect(() => {
    if (!isOpen) {
      dispatch(resetEditableDataset());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <CommonModal isOpen={isOpen} onClose={onClose} wrapperClassName={scss.modalWrapper}>
      <Form
        initialValues={addDatasetInitialValues}
        onSubmit={isEdit ? handleEditDataset : handleAddDataset}
        subscription={{ submitting: true, pristine: true }}
        validate={(values) => validate(values, addDatasetValidation())}
        render={({ handleSubmit }) => (
          <SonarDatasetModalForm onSubmit={handleSubmit} onClose={onClose} isEdit={isEdit} />
        )}
      />
    </CommonModal>
  );
};

export default memo(SonarDatasetModal);
